import SimpleModal from "app/components/Modals/SimpleModal";
import SortableTable from "app/components/SortableTable";
import Button from "app/storybookComponents/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { selectAllCompanyUsersById } from "app/containers/Global/slice";
import {
  acknowledgeHrConcernAlert,
  selectAcknowledgeHrConcernAlertStatus,
} from "../slice";
import DismissConcernModal from "./DismissConcernModal";
import { HrConcernAlert } from "../types";
import { HR_CONCERN_SEVERITY_MAP } from "../constants";
import { toast } from "react-toastify";
import { successNotificationOptions } from "utils/constants";

export interface ConcernAlertRow {
  alertId: number;
  userAccountId: number;
  threadId: string;
  isSummaryHidden?: boolean;
  date?: string;
  severity: HrConcernAlert["severity"];
  excerpt: string;
  detectedAt: string;
}

interface Props {
  show: boolean;
  onHide: () => void;
  activeAlerts: ConcernAlertRow[];
  onViewSummary: (alert: ConcernAlertRow) => void;
}

const HrConcernAlertsModal = ({
  show,
  onHide,
  activeAlerts,
  onViewSummary,
}: Props) => {
  const dispatch = useAppDispatch();
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const acknowledgeStatus = useAppSelector(
    selectAcknowledgeHrConcernAlertStatus
  );

  const [pendingAlertId, setPendingAlertId] = useState<number | null>(null);

  const isConfirmOpen = pendingAlertId !== null;

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
  };

  const getSeveritySortValue = (severity: HrConcernAlert["severity"]) =>
    HR_CONCERN_SEVERITY_MAP[severity]?.sortValue ?? 0;

  const getTableRows = () =>
    activeAlerts.map((alert) => {
      const user = usersById[alert.userAccountId];
      const fullName = user
        ? `${user.firstName} ${user.lastName}`.trim()
        : `User ${alert.userAccountId}`;

      return {
        employee: {
          displayValue: (
            <div>
              {user ? (
                <Link to={`/UserGuide/${user.userAccountId}`}>
                  <p>{fullName}</p>
                </Link>
              ) : (
                <p>{fullName}</p>
              )}
              {user?.jobTitle && (
                <p className="small-grey-text">{user.jobTitle}</p>
              )}
            </div>
          ),
          sortValue: fullName,
        },
        severity: {
          displayValue: (
            <span
              className={
                HR_CONCERN_SEVERITY_MAP[alert.severity]?.className ??
                "concern-badge"
              }
            >
              <FontAwesomeIcon
                icon={["far", "triangle-exclamation"]}
                className="me-1"
              />
              {HR_CONCERN_SEVERITY_MAP[alert.severity]?.label ?? alert.severity}
            </span>
          ),
          sortValue: getSeveritySortValue(alert.severity),
        },
        excerpt: !alert.isSummaryHidden
          ? {
              displayValue: (
                <p className="small-body-text">&ldquo;{alert.excerpt}&rdquo;</p>
              ),
              sortValue: alert.excerpt,
            }
          : "-",
        detectedAt: {
          displayValue: (
            <p className="small-body-text">{formatDate(alert.detectedAt)}</p>
          ),
          sortValue: alert.detectedAt,
        },
        action: {
          displayValue: (
            <div className="column-gap-8px">
              {!alert.isSummaryHidden && (
                <Button
                  variant="secondary-blue"
                  onClick={() => onViewSummary(alert)}
                >
                  View Summary
                </Button>
              )}
              <Button
                variant="secondary-blue"
                onClick={() => setPendingAlertId(alert.alertId)}
              >
                Dismiss
              </Button>
            </div>
          ),
          sortValue: "",
        },
      };
    });

  const headers = [
    { label: "Employee", key: "employee" },
    { label: "Severity", key: "severity" },
    { label: "Excerpt", key: "excerpt" },
    { label: "Detected", key: "detectedAt" },
    { label: "", key: "action", disableSort: true },
  ];

  const onConfirmDismiss = async (alertId: number) => {
    try {
      await dispatch(acknowledgeHrConcernAlert(alertId)).unwrap();
      setPendingAlertId(null);
      toast.success("Concern alert dismissed.", successNotificationOptions);
      if (activeAlerts.length === 1) {
        onHide();
      }
    } catch {
      toast.error(
        "Failed to dismiss the alert. Please try again.",
        successNotificationOptions
      );
    }
  };

  return (
    <>
      <DismissConcernModal
        isOpen={isConfirmOpen}
        alertId={pendingAlertId}
        onHide={() => setPendingAlertId(null)}
        isInProgress={acknowledgeStatus === "loading"}
        onConfirmClick={onConfirmDismiss}
      />
      <SimpleModal
        show={show && !isConfirmOpen}
        onHide={onHide}
        title="Active Concern Alerts"
      >
        <hr className="m-0" />
        <p>
          Review the concerning check-in responses below. Dismissing an alert
          removes it for all managers in your organization. This cannot be
          undone.
        </p>
        <SortableTable
          columnHeaders={headers}
          rows={getTableRows()}
          tableClassName="hr-concern-alerts-table"
          hideHeaderLineWhenEmpty
        />
      </SimpleModal>
    </>
  );
};

export default HrConcernAlertsModal;
